import { PaymentElement, useElements } from '@stripe/react-stripe-js';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
} from 'react';

// TA-10957: Stripe.js is preloaded at app start, so going offline afterwards
// leaves Stripe.js available but the PaymentElement iframe pending forever —
// `onReady` never fires and the checkout shows an empty payment form. Cap the
// wait and route failures to the same "Payment unavailable" fallback.
export const PAYMENT_ELEMENT_READY_TIMEOUT_MS = 10000;

/**
 * Lets the checkout stack (which owns the fallback) hear about element
 * failures from screens nested deep inside its navigator. Defaults to a no-op
 * so the element still renders outside a provider.
 */
export const PaymentElementFailureContext = createContext<
  (error: Error) => void
>(() => {});

type Props = React.ComponentProps<typeof PaymentElement> & {
  readyTimeoutMs?: number;
};

/**
 * Drop-in PaymentElement that reports a load error, or a missing `ready`
 * event within `readyTimeoutMs`, to PaymentElementFailureContext.
 */
export const WatchedPaymentElement: React.FC<Props> = ({
  readyTimeoutMs = PAYMENT_ELEMENT_READY_TIMEOUT_MS,
  onReady,
  onLoadError,
  ...props
}) => {
  const reportFailure = useContext(PaymentElementFailureContext);
  // Null until Stripe.js resolves; the element only mounts its iframe after
  // that, and Stripe.js load failures already have their own timeout
  const elements = useElements();
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Stable identity so the effect can list it without restarting the timer
  const clearTimer = useCallback(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
  }, []);

  useEffect(() => {
    if (!elements) return;
    timer.current = setTimeout(() => {
      timer.current = null;
      reportFailure(
        new Error(`PaymentElement not ready after ${readyTimeoutMs}ms`)
      );
    }, readyTimeoutMs);
    return clearTimer;
  }, [clearTimer, elements, readyTimeoutMs, reportFailure]);

  return (
    <PaymentElement
      {...props}
      onReady={(element) => {
        clearTimer();
        onReady?.(element);
      }}
      onLoadError={(event) => {
        clearTimer();
        onLoadError?.(event);
        reportFailure(
          new Error(
            `PaymentElement failed to load: ${event.error?.message ?? 'unknown'}`
          )
        );
      }}
    />
  );
};
